import { Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import { compact, filter, includes, map, sortBy, values } from 'lodash';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { requisitesApi, shopsApi, counterpartiesApi } from 'api';
import {
  CustomersFormSection,
  DataWrapper,
  EntityDetailsPage,
  ImportListDialog,
  RequisitesInfo,
  FormikRadioGroup,
  ShopSelect,
  FormikArray,
  FormikSelectProps,
  FormikSelect,
  FormikSelectOption,
} from 'components';
import { NEW_ID } from 'constants/common.constants';
import { ROUTE_PATH } from 'constants/routes';
import { QueryKey, RequisitesDistributionMode } from 'enums';
import { useCustomersForm, useShopsQuery, useUser } from 'hooks';
import { TranslationNamespace } from 'i18n';
import {
  Counterparty,
  CounterpartyCustomer,
  CounterpartyRequisites,
} from 'types';

type RequisitesSelectProps = {
  name: string;
  label: string;
  options: FormikSelectOption[];
  exclude?: string[];
} & Partial<FormikSelectProps>;

const RequisitesSelect: React.FC<RequisitesSelectProps> = ({
  name,
  label,
  options = [],
  exclude = [],
  ...rest
}) => {
  const availableOptions = useMemo(
    () => filter(options, (option) => !includes(exclude, option.value)),
    [options, exclude],
  );
  return (
    <FormikSelect
      noneOption
      label={label}
      {...rest}
      name={name}
      options={availableOptions}
      sx={{ mr: 1, flex: 1, minWidth: 400 }}
    />
  );
};

const RequisitesForm = ({ counterpartyId }: { counterpartyId?: string }) => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'features.counterparties',
  });

  const {
    values: { shopId, requisites },
  } = useFormikContext<Partial<Counterparty>>();

  const queryResultRequisites = useQuery(
    [QueryKey.Requisites, shopId, counterpartyId],
    () => requisitesApi.getAllForCounterparties(shopId!, counterpartyId),
    { enabled: !!shopId },
  );

  const requisitesOptions = useMemo(
    () =>
      map(queryResultRequisites.data, (requisite) => ({
        value: requisite.id,
        label: (
          <RequisitesInfo
            compact
            canCopy={false}
            requisite={requisite}
            showTrader
          />
        ),
      })),
    [queryResultRequisites.data],
  );

  const renderRequisitesSelect = useCallback(
    ({ item, index }: { item: CounterpartyRequisites; index: number }) => {
      const exclude = map(
        filter(requisites, (r) => r.requisitesId !== item.requisitesId),
        (t) => t.requisitesId,
      );

      return (
        <RequisitesSelect
          name={`requisites[${index}].requisitesId`}
          label={t('fields.requisites')}
          exclude={exclude}
          options={requisitesOptions}
          required
        />
      );
    },
    [t, requisitesOptions, requisites],
  );

  return (
    <div className="tw-pb-4">
      <DataWrapper
        queryResult={queryResultRequisites}
        emptyViewText={t('fields.no_requisites')}
      >
        <FormikArray
          addItemLabel={t('buttons.add_requisites')}
          name="requisites"
          sortable
          removable
          defaultValue={''}
          ItemComponent={renderRequisitesSelect}
        />
      </DataWrapper>
    </div>
  );
};

export const CounterpartyDetails: React.FC = () => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'features.counterparties',
  });
  const { id } = useParams();
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);
  const { role, isAdmin, isTechOperator } = useUser();

  const [importOpen, setImportOpen] = useState(false);

  const queryResultShops = useShopsQuery(
    QueryKey.Shops,
    shopsApi.getAllAsRole(role),
  );

  const initialValues: Partial<Counterparty> = useMemo(
    () => ({
      shopId: '',
      requisites: [],
      customers: [],
      requisitesDistributionMode: RequisitesDistributionMode.Random,
    }),
    [],
  );

  const isNew = useMemo(() => id === NEW_ID, [id]);

  const listUrl = useMemo(() => {
    if (isTechOperator) {
      return ROUTE_PATH.TECH_OPERATOR.COUNTERPARTIES;
    } else if (isAdmin) {
      return ROUTE_PATH.ADMIN.COUNTERPARTIES;
    }
  }, [isAdmin, isTechOperator]);

  const {
    customersSchema,
    renderCustomerItem,
    handleImportIdConfirm,
    handleError,
  } = useCustomersForm({
    onClose: () => setImportOpen(false),
  });

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        shopId: Yup.string().required(tCommon('errors.required')),
        requisitesDistributionMode: Yup.string().optional().nullable(),
        requisites: Yup.array().of(
          Yup.object().shape({
            requisitesId: Yup.string().required(tCommon('errors.required')),
          }),
        ),
        customers: customersSchema,
      }),
    [customersSchema, tCommon],
  );

  const initialValuesSelect = useCallback(
    (item: Counterparty): Counterparty => ({
      ...item,
      requisites: sortBy(item.requisites, 'priority'),
      customers: item.customers || [],
    }),
    [],
  );

  const formatData = useCallback(
    (data: Counterparty) => ({
      ...data,
      requisites: map(data.requisites, (item) => ({
        id: item.id,
        requisitesId: item.requisitesId,
      })),
      customers: map(
        compact(map(data.customers, 'customerId')),
        (customerId) => ({ customerId }),
      ),
    }),
    [],
  );

  const onBeforeUpdateMutation = useCallback(
    ({
      id,
      data,
    }: {
      id: string;
      data: Counterparty;
    }): { id: string; data: Counterparty } => ({
      id,
      data: formatData(data),
    }),
    [formatData],
  );

  const requisitesDistributionModeOptions = useMemo(
    () =>
      map(
        values(RequisitesDistributionMode),
        (mode: RequisitesDistributionMode) => ({
          value: mode,
          label: t(`distribution_modes.${mode}`, { defaultValue: mode }),
        }),
      ),
    [t],
  );

  return (
    <EntityDetailsPage
      id={id!}
      api={counterpartiesApi}
      apiAsRole={false}
      listUrl={listUrl}
      title={t(isNew ? 'create.title' : 'edit.title')}
      initialValues={initialValues}
      queryKey={QueryKey.Counterparties}
      initialValuesSelect={initialValuesSelect}
      onBeforeCreateMutation={formatData}
      onBeforeUpdateMutation={onBeforeUpdateMutation}
      onError={handleError}
      validationSchema={validationSchema}
      formClassName="tw-max-w-xl"
    >
      {({ formik }) => (
        <Fragment>
          <ShopSelect
            name="shopId"
            shops={queryResultShops.data}
            required
            disabled={!isNew}
          />
          <FormikRadioGroup
            label={t('fields.distribution_mode')}
            name="requisitesDistributionMode"
            options={requisitesDistributionModeOptions}
          />
          <CustomersFormSection
            name="customers"
            label={t('fields.customers')}
            addItemLabel={t('buttons.add_customer')}
            defaultValue={{ customerId: '' } as CounterpartyCustomer}
            ItemComponent={renderCustomerItem}
            importButtons={[
              {
                label: t('buttons.import_customers'),
                onClick: () => setImportOpen(true),
              },
            ]}
          />
          <ImportListDialog
            open={importOpen}
            onClose={() => setImportOpen(false)}
            onConfirm={(ids) =>
              handleImportIdConfirm(
                formik.setFieldValue,
                formik.setFieldTouched,
                formik.values.customers ?? [],
                ids,
              )
            }
          />
          <Typography variant="subtitle1">{t('fields.requisites')}</Typography>
          <RequisitesForm
            key={formik.values.shopId}
            counterpartyId={isNew ? undefined : id}
          />
        </Fragment>
      )}
    </EntityDetailsPage>
  );
};
